import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { API_STATUS_CODES } from '@constants/common.constants';
import { environmentSelector } from '@selectors/commonSelectors';
import { IFilterOptions, ITag, ITagOption, IuseAdvanceSearchProps, TagType } from '../constants/ticketlist.interface';
import { useApiHandler } from '../hooks/useApiHandler';
import { useFormHandler } from '../hooks/useFormHandler';

import { TICKET_LIST_ACTION_TYPES } from '@constants/actionTypes/ticketListActionTypes';
import {
    DEFAULT_PAYLOAD,
    TICKET_LIST_PAGE_INFO,
    TICKET_LIST_SEARCH_VARS,
    TicketApiType,
    advanceFormKeys,
    advanceSearchValue,
    customFieldTypes,
    defaultSearchValue,
    pendingReport,
} from '../constants/ticketlist.constants';
import {
    TimezoneInfo,
    getItemFromSessionStorage,
    handleChange,
    highlightFields,
    parseQueryString,
    setItemToSessionStorage,
    setTicketsSessionData,
} from '../constants/ticketlist.utils';

export const useAdvanceSearch = (props: IuseAdvanceSearchProps) => {
    const { setResetTrigger } = props;
    const { advanceSearchForm } = useFormHandler();
    const [isUpdatedFromSession, setIsUpdatedFromSession] = useState(false);
    const [highlightedFields, setHightlightedFields] = useState<IFilterOptions>();
    const [workgroupOptions, setWorkgroupOptions] = useState([]);
    const [areTagsLoaded, setAreTagsLoaded] = useState(false);

    const localDates = useSelector(environmentSelector)?.localDates;
    const timezoneInfo: TimezoneInfo | undefined = useMemo(() => {
        if (!localDates) return undefined;
        return {
            timezoneOffsetMs: Number(localDates.timezoneOffsetMs) || 0,
            dstSavingsMs: Number(localDates.dstSavingsMs) || 0,
        };
    }, [localDates]);

    const {
        listAPI,
        ticketAssigneeAPI,
        ticketRequestorsAPI,
        createdForAPI,
        findByCategoryAPI,
        customFieldOptionsAPI,
        defaultListAPI,
        findWorkgroupByTagName,
        tagsListAPI,
    } = useApiHandler();
    const {
        controller: { ticketQueues, roleList, customFields, tkmWorkflows },
        ticketCategory,
        customFieldOptions,
        tagsList,
    } = useSelector((state: any) => state.ticketMgmt.ticketsList) || {};

    const { handleSubmit, watch, setValue, reset, register, formState } = advanceSearchForm;
    const { isDirty } = formState;
    const formData = watch();
    const dispatch = useDispatch();

    // Get tag options based on type - data is already filtered and sorted from API
    const getTagOptionsByType = (type: 'USER' | 'WORKGROUP'): ITagOption[] => {
        if (!tagsList || typeof tagsList !== 'object') return [];

        const tags = tagsList[type];
        if (!Array.isArray(tags)) return [];

        return tags.map((tag: ITag) => ({
            value: tag.id,
            text: tag.name,
            ...tag,
        }));
    };

    const workgroupTagListOptions = getTagOptionsByType(TagType.WORKGROUP);
    const userTagListOptions = getTagOptionsByType(TagType.USER);

    useEffect(() => {
        setWorkgroupOptionList(ticketQueues);
    }, [ticketQueues]);

    useEffect(() => {
        const ticketListSearchVarsV1 = sessionStorage.getItem(TICKET_LIST_SEARCH_VARS);
        const isReportPageCall =
            document.referrer.indexOf(pendingReport) > 0 || parseQueryString().fromAgeingReports === 'true';
        const updatedValues = {};
        if (ticketListSearchVarsV1 !== 'undefined') {
            let parsedSearchCriteria = ticketListSearchVarsV1 ? JSON.parse(ticketListSearchVarsV1) : {};
            if (parsedSearchCriteria) {
                Object.entries(parsedSearchCriteria).forEach(([key, rawValue]: any) => {
                    let value = rawValue;
                    const isArrayField = [
                        'ticketType',
                        'ticketStatus',
                        'ticketQueues',
                        'ticketPriority',
                        'watchStatusAdvanced',
                        'confidentialTicketsFilter',
                    ].includes(key);
                    const isDateField = ['submittedBefore', 'submittedAfter'].includes(key);

                    if (isDateField) {
                        value = rawValue ? new Date(rawValue) : '';
                    } else if (isArrayField) {
                        value = rawValue ? rawValue.split(',') : [];
                        if (key === 'ticketType' && parsedSearchCriteria.ticketCategorySelect?.length > 0) {
                            findByCategoryAPI(dispatch, {
                                category: parsedSearchCriteria.ticketCategorySelect,
                            });
                        }
                    } else if (key === 'customFieldSelectVal') {
                        value = rawValue || '';
                        if (new Date(value).toString() !== 'Invalid Date') {
                            value = new Date(value);
                        }
                    }
                    if (key === 'customFieldSelectValOptionId') {
                        value = rawValue || '';
                        if (value.length > 0) {
                            customFieldOptionsAPI(dispatch, {
                                id: getCustomField(parsedSearchCriteria.customFieldSelect)?.cfieldParamName,
                            });
                        }
                    }
                    setValue(key, value);
                    updatedValues[key] = value;
                    if (advanceFormKeys.includes(key) && value !== advanceSearchValue[key]) {
                        setIsUpdatedFromSession(true);
                    }
                });
                setHightlightedFields(highlightFields(updatedValues));
                setItemToSessionStorage(TICKET_LIST_SEARCH_VARS, { ...parsedSearchCriteria, isReportPageCall });
            }
        } else {
            setItemToSessionStorage(TICKET_LIST_SEARCH_VARS, {
                ...DEFAULT_PAYLOAD,
                isReportPageCall,
                isSimplifiedView: false,
            });
        }
    }, []);

    useEffect(() => {
        return () => {
            setTicketsSessionData(formData, false, timezoneInfo);
        };
    }, [formData, timezoneInfo]);

    const fetchList = useCallback(
        async (formData: any) => {
            const payload = setTicketsSessionData(formData, false, timezoneInfo);
            const isFormDirty = isDirty || isUpdatedFromSession;
            const {
                currentPage = 1,
                sortField = '',
                sortOrder = '',
            } = getItemFromSessionStorage(TICKET_LIST_PAGE_INFO) || {};
            setHightlightedFields(highlightFields(formData));
            await listAPI(dispatch, {
                ...DEFAULT_PAYLOAD,
                ...payload,
                pageNumber: isFormDirty ? 1 : currentPage,
                sortBy: isFormDirty ? '' : sortField,
                sortOrder: isFormDirty ? '' : sortOrder,
            });
        },
        [isDirty, isUpdatedFromSession, timezoneInfo],
    );

    const handleTicketCategoryChange = async (value: any) => {
        if (value) {
            await findByCategoryAPI(dispatch, {
                category: value,
            });
        } else {
            dispatch({ type: TICKET_LIST_ACTION_TYPES.SET_TICKET_CATEGORY, payload: [] });
        }
        handleChange('ticketCategorySelect', value, setValue);
    };

    const handleWorkgroupChange = async (value: any) => {
        const tagName = workgroupTagListOptions.find(tag => tag.id === Number(value))?.name;
        if (tagName) {
            const { data, statusCode } = await findWorkgroupByTagName(dispatch, {
                tagName,
            });
            let updatedWorkgroupOptions = [];
            if (statusCode === API_STATUS_CODES.OK) {
                updatedWorkgroupOptions = data.map((queue: { name: string }) => ({
                    key: queue.name,
                    value: queue.name,
                    ...queue,
                }));
            }
            setWorkgroupOptions(updatedWorkgroupOptions);
        } else {
            setWorkgroupOptionList(ticketQueues);
        }
        handleChange('workGroupBytagId', value, setValue);
        handleChange('ticketQueues', [], setValue);
    };

    const onCancel = useCallback(async () => {
        const payload = { ...DEFAULT_PAYLOAD, csrftoken: window.csrf_token };
        setItemToSessionStorage(TICKET_LIST_SEARCH_VARS, {
            ...DEFAULT_PAYLOAD,
            userFilter: defaultSearchValue.ticketAssignment,
            filterticketStatus: defaultSearchValue.ticketStatus,
            userticketQueues: defaultSearchValue.workgroup,
            watchStatusBasic: defaultSearchValue.watchStatusBasic,
        });
        setItemToSessionStorage(TICKET_LIST_PAGE_INFO, { sortBy: '', sortOrder: '', currentPage: 1 });
        setHightlightedFields({} as IFilterOptions);
        setResetTrigger(prev => !prev);
        defaultListAPI(dispatch, payload);
        dispatch({ type: TICKET_LIST_ACTION_TYPES.SET_TICKET_LIST_API_TYPE, payload: TicketApiType.DEFAULTLIST });
        reset();
    }, [reset]);

    const getCustomField = (value: customFieldTypes) =>
        customFields?.find((customField: { cfieldParamName: string }) => customField.cfieldParamName === value);

    const handleCustomFieldChange = async (value: customFieldTypes) => {
        if (getCustomField(value)?.cfieldType === customFieldTypes.OPTION_LIST) {
            await customFieldOptionsAPI(dispatch, { id: getCustomField(value)?.cfieldParamName });
        }
        setValue('customFieldSelectVal', '');
        handleChange('customFieldSelect', value, setValue);
    };

    const isCustomTextInput =
        getCustomField(formData.customFieldSelect as customFieldTypes)?.cfieldType === customFieldTypes.TEXT_BOX;
    const isCustomDateInput =
        getCustomField(formData.customFieldSelect as customFieldTypes)?.cfieldType === customFieldTypes.DATE_INPUT;
    const isCustomOptionInput =
        getCustomField(formData.customFieldSelect as customFieldTypes)?.cfieldType === customFieldTypes.OPTION_LIST;

    const handleCustomFieldValueChange = (value: string) => {
        const updateCustomFieldValue = (val: string, optionId: string) => {
            handleChange('customFieldSelectVal', val, setValue);
            handleChange('customFieldSelectValOptionId', optionId, setValue);
        };

        if (isCustomTextInput || isCustomDateInput) {
            updateCustomFieldValue(value, '');
        } else if (isCustomOptionInput) {
            const selectedOption = customFieldOptions.find(option => String(option.id) === value);
            updateCustomFieldValue(selectedOption?.value || '', value);
        }
    };

    const fetchAssignee = async (assignee: string) => {
        handleChange('assignee', assignee, setValue);
        const { data } = await ticketAssigneeAPI(dispatch, {
            csrftoken: window.csrf_token,
            assigneeName: assignee,
        });
        return Object.entries(data).map(([key, value]: [string, string]) => ({ id: key, label: value }));
    };

    const fetchTicketRequestors = async (requestor: string) => {
        handleChange('requestedByName', requestor, setValue);
        const { data } = await ticketRequestorsAPI(dispatch, {
            userText: requestor,
            csrftoken: window.csrf_token,
        });
        return Object.entries(data).map(([key, value]: [string, string]) => ({ id: key, label: value }));
    };

    const fetchCreatedFor = async (createdFor: string) => {
        handleChange('requester', createdFor, setValue);
        const { data } = await createdForAPI(dispatch, {
            csrftoken: window.csrf_token,
            roleId: formData.userRole,
            moduleName: roleList?.find((role: { id: string }) => role.id == formData.userRole)?.label,
            userText: createdFor,
        });
        return Object.entries(data).map(([key, value]: [string, string]) => ({ id: key, label: value }));
    };

    const setSelectedItem = (value: Array<any>, labelFieldName: string, idFieldName?: string) => {
        handleChange(labelFieldName, value[0]?.label, setValue);
        if (idFieldName) {
            handleChange(idFieldName, value[0]?.id, setValue);
        }
    };

    const setWorkgroupOptionList = ticketQueues => {
        const updatedWorkgroupOptions =
            ticketQueues?.map(({ name, ...rest }) => ({
                key: name,
                value: name,
                name,
                ...rest,
            })) || [];
        setWorkgroupOptions(updatedWorkgroupOptions);
    };

    const loadTagsIfNeeded = async () => {
        if (!areTagsLoaded) {
            await tagsListAPI(dispatch, {});
            setAreTagsLoaded(true);
        }
    };

    return {
        handleSubmit,
        watch,
        setValue,
        reset,
        register,
        formState,
        onSubmit: fetchList,
        workgroupOptions,
        fetchAssignee,
        fetchTicketRequestors,
        roleList,
        tagsList,
        customFields,
        tkmWorkflows,
        fetchCreatedFor,
        onCancel,
        handleTicketCategoryChange,
        ticketCategory,
        customFieldOptions,
        getCustomField,
        handleCustomFieldChange,
        isCustomTextInput,
        isCustomDateInput,
        isCustomOptionInput,
        workgroupTagListOptions,
        userTagListOptions,
        values: formData,
        setSelectedItem,
        highlightedFields,
        handleWorkgroupChange,
        handleCustomFieldValueChange,
        loadTagsIfNeeded,
    };
};
